import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../helpers/api';
import { ADMIN_ROLE, ENDPOINTS, PROCESSOR_ROLE } from '../../helpers/constants';
import useAppChoices from '../../hooks/useAppChoices';
import AppModal from '../common/AppModal';

const DEFAULT_PROCESSOR_ROLES = [PROCESSOR_ROLE, ADMIN_ROLE];
const INACTIVE_PROCESSOR_COLOR = '#b91c1c';

const isInactiveProcessor = user => !user.isEnabled || !DEFAULT_PROCESSOR_ROLES.includes(user.role);

const toProcessorOption = user => {
  const name = user.name || user.email;
  const isInactive = isInactiveProcessor(user);
  return { value: user._id, label: isInactive ? `${name} (Inactive)` : name, isInactive };
};

// Looks like the modal's .county-input-custom; "&&" outranks the .select-organization overrides
const processorSelectStyles = {
  control: (base, { isFocused }) => ({
    ...base,
    '&&': {
      minHeight: 44,
      border: `2px solid ${isFocused ? '#237fff' : '#e5e7ea'}`,
      boxShadow: isFocused ? '0 0 0 3px rgba(35, 127, 255, 0.12)' : 'none',
      fontSize: '14px !important'
    }
  }),
  menuPortal: base => ({ ...base, zIndex: 1060 }),
  multiValue: (base, { data }) => (data.isInactive ? { ...base, backgroundColor: '#fee2e2' } : base),
  multiValueLabel: (base, { data }) => (data.isInactive ? { ...base, color: INACTIVE_PROCESSOR_COLOR } : base)
};

/**
 * Adds a county or edits an existing one: its name and the default processors of its Lien Search orders.
 * `county` is the county being edited; leave it empty to add one.
 */
const CountyFormModal = ({ show, county, initialName = '', initialProcessors = [], onHide, onSaved }) => {
  const isEdit = Boolean(county);
  const users = useAppChoices('users');
  const [name, setName] = useState('');
  const [selectedProcessors, setSelectedProcessors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) return;
    setName(initialName);
    setSelectedProcessors(initialProcessors.map(toProcessorOption));
  }, [show]);

  const processorOptions = useMemo(
    () =>
      (users || [])
        .filter(user => !isInactiveProcessor(user))
        .map(toProcessorOption)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users]
  );

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    setSubmitting(true);
    const { response, error } = await makeRESTApiRequest({
      endpoint: isEdit ? ENDPOINTS.COUNTY_WITH_NAME(county) : ENDPOINTS.CREATE_COUNTY,
      method: isEdit ? 'PUT' : 'POST',
      requestBody: { county: trimmedName, processors: selectedProcessors.map(option => option.value) }
    });
    setSubmitting(false);

    if (error) {
      toast.error(error);
      return;
    }

    toast.success(isEdit ? 'County updated successfully' : 'County added successfully');
    onSaved(response);
  };

  return (
    <AppModal
      show={show}
      onHide={onHide}
      onSubmit={handleSubmit}
      heading={isEdit ? 'Edit County' : 'Add County'}
      submitLabel={isEdit ? 'Save' : 'Add County'}
      submitting={submitting}
      submitDisabled={!name.trim()}
    >
      <Form.Group className="mb-3">
        <Form.Label className="modal-form-label">
          County Name <span className="text-danger">*</span>
        </Form.Label>
        <input
          type="text"
          className="county-input-custom"
          placeholder="Enter county name…"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && name.trim() && !submitting && handleSubmit()}
          disabled={submitting}
          autoFocus
        />
      </Form.Group>
      <Form.Group className="mb-0">
        <Form.Label className="modal-form-label">Default Processor</Form.Label>
        <Select
          isMulti
          placeholder="Select processors"
          isDisabled={submitting}
          options={processorOptions}
          value={selectedProcessors}
          onChange={options => setSelectedProcessors(options || [])}
          closeMenuOnSelect={false}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
          noOptionsMessage={() => 'No processors'}
          className="basic-multi-select select-organization"
          classNamePrefix="select"
          styles={processorSelectStyles}
        />
      </Form.Group>
    </AppModal>
  );
};

export default CountyFormModal;
